import Vue from 'vue';
import Toast from '@/components/layout/utils/Toast.vue';

// Create a Vue instance for toast container
const ToastContainer = Vue.extend({
  name: 'ToastContainer',
  data() {
    return {
      toasts: []
    };
  },
  methods: {
    addToast(options) {
      const toastId = Date.now().toString();

      // Create toast instance
      const toast = {
        id: toastId,
        ...options
      };

      // Add to toasts array
      this.toasts.push(toast);

      // Return toast id for potential future reference
      return toastId;
    },

    removeToast(id) {
      const index = this.toasts.findIndex(toast => toast.id === id);
      if (index !== -1) {
        this.toasts.splice(index, 1);
      }
    }
  },
  render(h) {
    return h('div', { class: 'toast-service-container' }, 
      this.toasts.map(toast => {
        return h(Toast, {
          key: toast.id,
          props: {
            title: toast.title || '',
            message: toast.message,
            type: toast.type || 'info',
            duration: toast.duration !== undefined ? toast.duration : 3000,
            position: toast.position || 'top-end',
            showCloseButton: toast.showCloseButton !== false,
            showProgressBar: toast.showProgressBar !== false,
            icon: toast.icon !== false,
            showLogo: toast.showLogo !== false,
            logoPath: toast.logoPath || '/favicon.ico',
            customClass: toast.customClass || ''
          },
          on: {
            closed: () => {
              this.removeToast(toast.id);
              if (toast.onClose) {
                toast.onClose();
              }
            }
          },
          ref: `toast-${toast.id}`
        });
      })
    );
  },
  mounted() {
    // When a toast is added, call its show method
    this.$watch('toasts', (newToasts, oldToasts) => {
      if (newToasts.length > oldToasts.length) {
        // A new toast was added
        const newToast = newToasts[newToasts.length - 1];
        this.$nextTick(() => {
          const toastRef = this.$refs[`toast-${newToast.id}`];
          if (toastRef) {
            toastRef.show();
          }
        });
      }
    }, { deep: true });
  }
});

// Create and mount the toast container
let containerInstance = null;

function ensureContainer() {
  if (!containerInstance) {
    const container = document.createElement('div');
    container.id = 'toast-service-container';

    // Add inline styles to ensure visibility
    container.style.position = 'fixed';
    container.style.top = '0';
    container.style.right = '0';
    container.style.zIndex = '9999999'; // Increased z-index to match fallback toast
    container.style.width = 'auto';
    container.style.height = 'auto';
    container.style.pointerEvents = 'none';
    container.style.display = 'block !important';
    container.style.visibility = 'visible !important';

    document.body.appendChild(container);

    containerInstance = new ToastContainer();
    containerInstance.$mount('#toast-service-container');
  }

  return containerInstance;
}

// Helper function to fade out and remove a toast
function fadeOutAndRemoveToast(toast) {
  if (!toast) return;

  // Add a transition for opacity
  const innerToastElement = toast.firstChild;
  if (innerToastElement) {
    // Add transition style
    innerToastElement.style.transition = 'opacity 0.3s ease-in, transform 0.3s ease-in';
    innerToastElement.style.opacity = '0';
    innerToastElement.style.transform = 'translateY(-20px) scale(0.8)';

    // Remove the element after the transition completes
    setTimeout(() => {
      toast.remove();
    }, 300); // Match the transition duration
  } else {
    toast.remove(); // Fallback if no inner element
  }
}

// Create a fallback toast element
function createFallbackToast(options) {
  const { title, message, type, duration = 3000, position, customClass, showLogo = true, logoPath = '/favicon.ico' } = options;
  const id = `fallback-toast-${Date.now()}`;

  // Check if fallback already exists with this ID
  if (document.getElementById(id)) {
    return;
  }

  // Determine colors based on type
  let bgColor = '#fff';
  let textColor = '#333';
  let borderColor = '#ddd';
  let iconColor = '#333';

  switch (type) {
    case 'success':
      bgColor = '#d4edda';
      textColor = '#155724';
      borderColor = '#2ecc71';
      iconColor = '#2ecc71';
      break;
    case 'warning':
      bgColor = '#fff3cd';
      textColor = '#856404';
      borderColor = '#f39c12';
      iconColor = '#f39c12';
      break;
    case 'error':
      bgColor = '#f8d7da';
      textColor = '#721c24';
      borderColor = '#e74c3c';
      iconColor = '#e74c3c';
      break;
    case 'info':
    default:
      bgColor = '#d1ecf1';
      textColor = '#0c5460';
      borderColor = '#3498db';
      iconColor = '#3498db';
      break;
  }

  // Determine position styles
  let positionStyles = 'top: 10px; right: 10px;'; // default top-end

  switch (position) {
    case 'top':
      positionStyles = 'top: 10px; left: 50%; transform: translateX(-50%);';
      break;
    case 'top-start':
      positionStyles = 'top: 10px; left: 10px;';
      break;
    case 'bottom':
      positionStyles = 'bottom: 10px; left: 50%; transform: translateX(-50%);';
      break;
    case 'bottom-start':
      positionStyles = 'bottom: 10px; left: 10px;';
      break;
    case 'bottom-end':
      positionStyles = 'bottom: 10px; right: 10px;';
      break;
  }

  // Create the fallback toast element
  const fallbackToast = document.createElement('div');
  fallbackToast.id = id;

  // Create the inner toast element
  const innerToast = document.createElement('div');
  innerToast.style.cssText = `
    position: fixed;
    ${positionStyles}
    background-color: ${bgColor};
    color: ${textColor};
    border-left: 4px solid ${borderColor};
    padding: 10px 15px;
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 9999999;
    min-width: 250px;
    max-width: 350px;
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
  `;

  // Add custom class if provided
  if (customClass) {
    innerToast.className = customClass;
  }

  // Create title element
  const titleElement = document.createElement('div');
  titleElement.style.cssText = 'font-weight: bold; margin-bottom: 5px;';
  titleElement.innerHTML = title ? title : '';

  // Create close button
  const closeButton = document.createElement('button');
  closeButton.style.cssText = `
    position: absolute;
    top: 8px;
    right: 8px;
    background: transparent;
    border: none;
    color: ${textColor};
    cursor: pointer;
    font-size: 14px;
    padding: 0;
    width: 20px;
    height: 20px;
  `;
  closeButton.innerHTML = '×';
  closeButton.onclick = function() {
    const toast = document.getElementById(id);
    if (toast) {
      fadeOutAndRemoveToast(toast);
    }
  };

  // Create logo element if showLogo is true
  if (showLogo) {
    const logoElement = document.createElement('div');
    logoElement.style.cssText = `
      margin-right: 8px;
      display: flex;
      align-items: center;
      float: left;
    `;

    const logoImg = document.createElement('img');
    logoImg.src = logoPath;
    logoImg.alt = 'Logo';
    logoImg.style.cssText = `
      width: 20px;
      height: 20px;
      object-fit: contain;
    `;

    logoElement.appendChild(logoImg);
    innerToast.appendChild(logoElement);
  }

  // Create message element
  const messageElement = document.createElement('div');
  messageElement.innerHTML = message;

  // Append elements to inner toast
  titleElement.appendChild(closeButton);
  innerToast.appendChild(titleElement);
  innerToast.appendChild(messageElement);

  // Append inner toast to fallback toast
  fallbackToast.appendChild(innerToast);

  // Append fallback toast to body
  document.body.appendChild(fallbackToast);

  // Add fade-out animation and then remove the toast after the specified duration
  if (duration) {
    setTimeout(() => {
      const toast = document.getElementById(id);
      if (toast) {
        fadeOutAndRemoveToast(toast);
      }
    }, duration);
  }

  return id;
}

// Toast service API
export const ToastService = {
  show(options) {
    // Ensure duration is set to 3000ms (3 seconds) if not specified
    if (options.duration === undefined) {
      options.duration = 3000;
    }

    try {
      // Try using the Vue component approach first
      const container = ensureContainer();
      const toastId = container.addToast(options);

      // Set a timeout to check if the toast is visible
      setTimeout(() => {
        // Check if any toast elements are visible in the DOM
        const toastElements = document.querySelectorAll('.toast');
        let isVisible = false;

        for (let i = 0; i < toastElements.length; i++) {
          const computedStyle = window.getComputedStyle(toastElements[i]);
          if (computedStyle.display !== 'none' && computedStyle.visibility !== 'hidden' && computedStyle.opacity !== '0') {
            isVisible = true;
            break;
          }
        }

        // If no toast is visible, create a fallback
        if (!isVisible) {
          createFallbackToast(options);
        }
      }, 500); // Give the Vue component 500ms to render

      return toastId;
    } catch (error) {
      // If Vue component approach fails, use the fallback
      return createFallbackToast(options);
    }
  },

  info(message, title = 'Information', options = {}) {
    return this.show({
      message,
      title,
      type: 'info',
      ...options
    });
  },

  success(message, title = 'Success', options = {}) {
    return this.show({
      message,
      title,
      type: 'success',
      ...options
    });
  },

  warning(message, title = 'Warning', options = {}) {
    return this.show({
      message,
      title,
      type: 'warning',
      ...options
    });
  },

  error(message, title = 'Error', options = {}) {
    return this.show({
      message,
      title,
      type: 'error',
      ...options
    });
  }
};

// Register as a global Vue property
Vue.prototype.$toast = ToastService;

// Also export as a global window property for non-Vue components
window.ToastService = ToastService;

// Update the existing Toast methods to use our new ToastService
// This ensures backward compatibility with existing code
const setupToastOverride = () => {
  if (window.Toast) {
    const originalToast = window.Toast;

    // Only override if it hasn't been overridden already
    if (!originalToast._overridden) {
      window.Toast = {
        ...originalToast,
        _overridden: true,
        fire: (options) => {
          const { icon, title, text, html } = options;
          return ToastService.show({
            type: icon || 'info',
            title: title || '',
            message: html || text || '',
            duration: options.timer || 3000,
            position: options.position || 'top-end',
            showCloseButton: options.showCloseButton !== false,
            showProgressBar: options.timerProgressBar !== false,
            showLogo: options.showLogo !== false,
            logoPath: options.logoPath || '/favicon.ico',
          });
        }
      };
    }
  } else {
    // If Toast is not available yet, try again later
    setTimeout(setupToastOverride, 100);
  }
};

// Start the override process
setupToastOverride();

export default ToastService;
